import React, { useState, useEffect, useRef } from "react";
import "./ContactSpecifics.css";
import PageIndicator from "./PageIndicator";

const originalOptions = {
    expertise: ["Frontend", "Backend", "Fullstack", "Design Engineer", "Mechanical Design Engineer", "Embedded Systems", "Firmware",
        "Hardware Design", "Automotive", "Medical Devices", "Industrial Automation", "IoT", "AI", "VR"
    ],
    languages: ["C", "C++", "C#,", ".NET", "Java", "Kotlin", "Rust", "Go", "Android", "Python", "Assembly", "MATLAB"],
    education: ["Bachelor's", "Master's", "PhD", "Self-taught", "Bootcamp"],
    experience: ["0-2 years", "3-5 years", "6-10 years", "10+ years"],
    projectType: ["Product Development", "Prototype Design", "Firmware Update", "Debugging", "System Integration", "Testing & Validation", "Technical Consulting", "Legacy System Modernization"],
    timeline: ["ASAP", "1-3 months", "3-6 months", "6-12 months", "12+ months", "Flexible"],
    budget: ["< 10 000 SEK", "10 000 - 25 000 SEK", "25 000 - 50 000 SEK", "50 000 - 100 000 SEK", "100 000+ SEK", "Let's discuss"]
};


const ContactSpecifics = ({ joinOrHire, currentMobilePage, onMobilePageChange, onSubmit, onReview, isSubmitting, storedFormData, getContactDetails }) => {
    const [currentPage, setCurrentPage] = useState(0);
    const [formData, setFormData] = useState({
        expertise: [],
        languages: [],
        experience: [],
        education: [],
        description: '',
        projectType: [],
        timeline: [],
        budget: []
    });

    // State for custom skills input
    const [customSkills, setCustomSkills] = useState({
        expertise: [],
        languages: [],
        education: [],
        experience: [],
        projectType: [],
        timeline: [],
        budget: []
    });

    // State for editing custom skills
    const [editingSkill, setEditingSkill] = useState({
        category: null,
        index: null,
        value: ''
    });

    // Ref to track when input fields appear
    const inputRefs = useRef({});

    // Restore form data when coming back from review
    useEffect(() => {
        if (storedFormData?.contactSpecifics) {
            // Restore the main form data
            setFormData(storedFormData.contactSpecifics);
            // Restore the custom skills
            if (storedFormData.contactSpecifics.customSkills) {
                setCustomSkills(storedFormData.contactSpecifics.customSkills);
            }

            // Restore selected values for each category
            const restoredSelectedValues = {};
            ['expertise', 'languages', 'education', 'experience', 'projectType', 'timeline', 'budget'].forEach(category => {
                const selectedInFormData = storedFormData.contactSpecifics[category] || [];
                restoredSelectedValues[category] = selectedInFormData;
            });
            setSelectedValues(restoredSelectedValues);

            // Recalculate available values based on restored selected values
            const restoredAvailableValues = {};
            ['expertise', 'languages', 'education', 'experience', 'projectType', 'timeline', 'budget'].forEach(category => {
                restoredAvailableValues[category] = originalOptions[category].filter(skill =>
                    !restoredSelectedValues[category].includes(skill)
                );
            });
            setAvailableValues(restoredAvailableValues);
        }
    }, [storedFormData]);

    const handleButtonClick = (e, category, explicitValue) => {
        if (e) e.preventDefault();
        const value = explicitValue ?? e.target.value;
        const isSelected = formData[category].includes(value);

        if (['expertise', 'languages', 'education', 'experience', 'projectType', 'timeline', 'budget'].includes(category)) {
            if (isSelected) {
                // Remove from selected
                const newSelected = selectedValues[category].filter(item => item !== value);
                setSelectedValues(prev => ({
                    ...prev,
                    [category]: newSelected
                }));
                setFormData(prev => ({
                    ...prev,
                    [category]: prev[category].filter(item => item !== value)
                }));
                // Recalculate available list maintaining original order
                setAvailableValues(prev => ({
                    ...prev,
                    [category]: originalOptions[category].filter(skill => !newSelected.includes(skill))
                }));
            } else {
                // Add to selected
                const newSelected = [...selectedValues[category], value];
                setSelectedValues(prev => ({
                    ...prev,
                    [category]: newSelected
                }));
                setFormData(prev => ({
                    ...prev,
                    [category]: [...prev[category], value]
                }));
                // Recalculate available list maintaining original order
                setAvailableValues(prev => ({
                    ...prev,
                    [category]: originalOptions[category].filter(skill => !newSelected.includes(skill))
                }));
            }
        } else {
            // Handle other categories normally
            setFormData(prev => ({
                ...prev,
                [category]: isSelected
                    ? prev[category].filter(item => item !== value)
                    : [...prev[category], value]
            }));
        }
    };

    const handleTextareaChange = (e) => {
        setFormData(prev => ({
            ...prev,
            description: e.target.value
        }));
    };

    // Function to auto-resize input width based on content
    const autoResizeInput = (e) => {
        const input = e.target;

        const tempSpan = document.createElement('span');
        tempSpan.style.visibility = 'hidden';
        tempSpan.style.position = 'absolute';
        tempSpan.style.whiteSpace = 'pre';
        tempSpan.style.font = window.getComputedStyle(input).font;
        tempSpan.style.padding = window.getComputedStyle(input).padding;
        tempSpan.style.border = window.getComputedStyle(input).border;
        tempSpan.style.boxSizing = window.getComputedStyle(input).boxSizing;

        const text = input.value || input.placeholder || '';
        tempSpan.textContent = text;

        document.body.appendChild(tempSpan);
        const width = Math.max(tempSpan.offsetWidth, 45); // 45px minimum width, reduced extra padding

        // Force the width with !important equivalent
        input.style.setProperty('width', width + 'px', 'important');
        input.style.setProperty('min-width', width + 'px', 'important');
        input.style.setProperty('max-width', 'none', 'important');

        document.body.removeChild(tempSpan);

        // Debug logging
        console.log('Auto-resize triggered:', { text, calculatedWidth: width, currentInputWidth: input.style.width });
    };

    const removeCustomSkill = (category, skillToRemove) => {
        setCustomSkills(prev => ({
            ...prev,
            [category]: prev[category].filter(skill => skill !== skillToRemove)
        }));
    };

    const startEditingSkill = (category, index, currentValue) => {
        console.log('startEditingSkill called:', { category, index, currentValue });
        setEditingSkill({
            category,
            index,
            value: currentValue
        });
        console.log('editingSkill state set to:', { category, index, value: currentValue });
    };

    const saveEditedSkill = (category, index) => {
        const newValue = editingSkill.value.trim();
        if (newValue) {
            if (index === null) {
                // Adding a new skill
                if (!customSkills[category].includes(newValue)) {
                    setCustomSkills(prev => ({
                        ...prev,
                        [category]: [...prev[category], newValue]
                    }));
                }
            } else {
                // Editing existing skill
                if (newValue !== customSkills[category][index]) {
                    setCustomSkills(prev => ({
                        ...prev,
                        [category]: prev[category].map((skill, i) =>
                            i === index ? newValue : skill
                        )
                    }));
                }
            }
        }
        setEditingSkill({ category: null, index: null, value: '' });
    };

    const cancelEditing = () => {
        setEditingSkill({ category: null, index: null, value: '' });
    };

    const handleEditKeyPress = (e, category, index) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveEditedSkill(category, index);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEditing();
        }
    };

    // Then initialize availableValues with the original options
    const [availableValues, setAvailableValues] = useState(originalOptions);

    const [selectedValues, setSelectedValues] = useState({
        expertise: [],
        languages: [],
        education: [],
        experience: [],
        projectType: [],
        timeline: [],
        budget: []
    });

    // Function to render available buttons (not selected)
    const renderAvailableButtons = (category) => {
        return (
            <>
                {availableValues[category].map(skill => (
                    <input
                        key={skill}
                        type="button"
                        value={skill}
                        onClick={(e) => handleButtonClick(e, category)}
                        className=""
                        aria-pressed={false}
                    />
                ))}

                {/* + button - show in available when no selected items */}
                {selectedValues[category].length === 0 &&
                    customSkills[category].length === 0 &&
                    !(editingSkill.category === category && editingSkill.index === null) && (
                        <input
                            type="button"
                            value="+"
                            onClick={() => {
                                setEditingSkill({ category: category, index: null, value: '' });
                            }}
                            className="editable-bubble"
                            placeholder="+"
                        />
                    )}
            </>
        );
    };

    // Function to render selected buttons
    const renderSelectedButtons = (category) => {
        return (
            <>
                {/* Render selected values */}
                {selectedValues[category].map(skill => (
                    <div key={skill} className="custom-skill-wrapper">
                        <input
                            type="button"
                            value={skill}
                            className="selected"
                            aria-pressed={true}
                            readOnly
                        />
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                handleButtonClick(e, category, skill);
                            }}
                            className="remove-skill-btn"
                            aria-label={`Remove ${skill}`}
                        >
                            ×
                        </button>
                    </div>
                ))}

                {/* Render existing custom skills */}
                {customSkills[category].map((skill, index) => (
                    editingSkill.category === category && editingSkill.index === index ? (
                        <div key={`edit-${index}`} className="editing-skill-wrapper">
                            <input
                                ref={(el) => {
                                    if (el) {
                                        inputRefs.current[`${category}-edit-${index}`] = el;
                                        setTimeout(() => autoResizeInput({ target: el }), 0);
                                    }
                                }}
                                type="text"
                                value={editingSkill.value}
                                onChange={(e) => setEditingSkill(prev => ({ ...prev, value: e.target.value }))}
                                onInput={autoResizeInput}
                                onKeyPress={(e) => handleEditKeyPress(e, category, index)}
                                onBlur={() => saveEditedSkill(category, index)}
                                className="editable-skill-input"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => cancelEditing()}
                                className="cancel-edit-btn"
                                aria-label="Cancel editing"
                            >
                                ×
                            </button>
                        </div>
                    ) : (
                        <div key={`custom-${index}`} className="custom-skill-wrapper">
                            <input
                                type="button"
                                value={skill}
                                onClick={() => startEditingSkill(category, index, skill)}
                                className="custom-skill-button"
                                title="Click to edit"
                            />
                            <button
                                type="button"
                                onClick={() => removeCustomSkill(category, skill)}
                                className="remove-skill-btn"
                                aria-label={`Remove ${skill}`}
                            >
                                ×
                            </button>
                        </div>
                    )
                ))}

                {/* Editable input when adding new skill */}
                {editingSkill.category === category && editingSkill.index === null && (
                    <div className="editing-skill-wrapper">
                        <input
                            ref={(el) => {
                                if (el) {
                                    inputRefs.current[`${category}-new`] = el;
                                    setTimeout(() => autoResizeInput({ target: el }), 0);
                                }
                            }}
                            type="text"
                            value={editingSkill.value}
                            onChange={(e) => setEditingSkill(prev => ({ ...prev, value: e.target.value }))}
                            onInput={autoResizeInput}
                            onFocus={autoResizeInput}
                            onKeyPress={(e) => handleEditKeyPress(e, category, null)}
                            onBlur={() => saveEditedSkill(category, null)}
                            className="editable-skill-input"
                            placeholder="Add..."
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={() => cancelEditing()}
                            className="cancel-edit-btn"
                            aria-label="Cancel editing"
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* + button - show in selected when there are selected items */}
                {(selectedValues[category].length > 0 || customSkills[category].length > 0) &&
                    !(editingSkill.category === category && editingSkill.index === null) && (
                        <input
                            type="button"
                            value="+"
                            onClick={() => {
                                setEditingSkill({ category: category, index: null, value: '' });
                            }}
                            className="editable-bubble"
                            placeholder="+"
                        />
                    )}
            </>
        );
    };

    const renderCategorySection = (category, title, selectionType = 'toggle') => {
        if (selectionType === 'radio') {
            // For radio buttons, just render the options in place
            return (
                <div key={category}>
                    <h4>{title}</h4>
                    <div className="button-container">
                        {renderRadioButtons(category)}
                    </div>
                </div>
            );
        }

        // For toggle buttons, use the two-section approach
        const hasSelectedItems = selectedValues[category].length > 0 ||
            customSkills[category].length > 0 ||
            (editingSkill.category === category && editingSkill.index === null);

        return (
            <div key={category}>
                <h4>{title}</h4>

                {/* Selected section - only show if there are selected items */}
                {hasSelectedItems && (
                    <div className="button-container">
                        {renderSelectedButtons(category)}
                    </div>
                )}

                {/* Available section */}
                <div className="button-container">
                    {renderAvailableButtons(category)}
                </div>
            </div>
        );
    };

    // Function to render radio buttons (single selection)
    const renderRadioButtons = (category) => {
        return (
            <>
                {originalOptions[category].map(skill => (
                    <input
                        key={skill}
                        type="button"
                        value={skill}
                        onClick={(e) => handleRadioButtonClick(e, category)}
                        className={selectedValues[category].includes(skill) ? 'selected' : ''}
                        aria-pressed={selectedValues[category].includes(skill)}
                    />
                ))}
            </>
        );
    };

    // Function to handle radio button clicks (single selection)
    const handleRadioButtonClick = (e, category) => {
        e.preventDefault();
        const value = e.target.value;
        const isSelected = selectedValues[category].includes(value);

        if (isSelected) {
            // Remove selection
            setSelectedValues(prev => ({
                ...prev,
                [category]: []
            }));
            setFormData(prev => ({
                ...prev,
                [category]: []
            }));
        } else {
            // Select new option (deselect previous)
            setSelectedValues(prev => ({
                ...prev,
                [category]: [value]
            }));
            setFormData(prev => ({
                ...prev,
                [category]: [value]
            }));
        }
    };

    const getPageContent = (pageIndex) => {
        if (joinOrHire) {
            // "Join us" pages
            switch (pageIndex) {
                case 0:
                    return {
                        title: "Areas of expertise",
                        content: (
                            <div style={{ marginTop: '18px' }}>
                                {renderCategorySection('expertise', 'What are your main areas of expertise?')}
                                {renderCategorySection('languages', 'Programming languages & technologies')}
                            </div>
                        )
                    };
                case 1:
                    return {
                        title: "Experience & Background",
                        content: (
                            <div style={{ marginTop: '18px' }}>
                                {renderCategorySection('experience', 'Years of Experience', 'radio')}
                                {renderCategorySection('education', 'Education Level', 'toggle')}
                            </div>
                        )
                    };
                case 2:
                    return {
                        title: "Tell us about yourself",
                        content: (
                            <div style={{ marginTop: '18px' }}>
                                <h4>Why do you want to join Qodes?</h4>
                                <textarea
                                    value={formData.description}
                                    onChange={handleTextareaChange}
                                    placeholder="Tell us about your career goals, what excites you about embedded development, and why you'd like to be part of our team..."
                                    rows="6"
                                    aria-label="Why do you want to join Qodes?"
                                />
                            </div>
                        )
                    };
                default:
                    return { title: "", content: null };
            }
        } else {
            // "Hire us" pages
            switch (pageIndex) {
                case 0:
                    return {
                        title: "Project Details",
                        content: (
                            <div style={{ marginTop: '18px' }}>
                                {renderCategorySection('projectType', 'What type of project do you need help with?')}
                                {renderCategorySection('languages', 'Technologies involved')}
                            </div>
                        )
                    };
                case 1:
                    return {
                        title: "Timeline & Budget",
                        content: (
                            <div style={{ marginTop: '18px' }}>
                                {renderCategorySection('timeline', 'Project Timeline', 'radio')}
                                {renderCategorySection('budget', 'Budget Range', 'radio')}
                            </div>
                        )
                    };
                case 2:
                    return {
                        title: "Project Description",
                        content: (
                            <div style={{ marginTop: '18px' }}>
                                <h4>Tell us about your project</h4>
                                <textarea
                                    value={formData.description}
                                    onChange={handleTextareaChange}
                                    placeholder="Describe your project goals, current challenges, technical requirements, and any specific expertise you're looking for..."
                                    rows="6"
                                    aria-label="Project description"
                                />
                            </div>
                        )
                    };
                default:
                    return { title: "", content: null };
            }
        }
    };

    const totalPages = 3; // Back to 3 pages since review is handled at ContactPage level
    const currentContent = getPageContent(currentPage);

    // Desktop navigation functions
    const nextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Mobile navigation functions
    const handleMobileNext = () => {
        if (currentMobilePage < 3) { // Back to 3 pages
            onMobilePageChange(currentMobilePage + 1);
        }
    };

    const handleMobilePrev = () => {
        if (currentMobilePage > 1) {
            onMobilePageChange(currentMobilePage - 1);
        } else {
            onMobilePageChange(0); // Go back to ContactDetails
        }
    };

    const handleSubmit = () => {
        // Get contact details using the passed function
        const contactDetails = getContactDetails ? getContactDetails() : {};

        // Validate required contact fields
        if (!contactDetails.name || !contactDetails.email || !contactDetails.phone) {
            alert('Please fill in all required contact information (name, email, and phone) before proceeding.');
            return;
        }

        // Collect all form data from both components
        if (onReview && typeof onReview === 'function') {
            // Prepare the complete form data with custom skills
            const completeFormData = {
                contactDetails: {
                    name: contactDetails.name || '',
                    email: contactDetails.email || '',
                    phone: contactDetails.phone || '',
                    files: contactDetails.files || []
                },
                contactSpecifics: {
                    ...formData,
                    customSkills: customSkills
                }
            };

            // Call the review function with complete data
            onReview(completeFormData);
        } else {
            // Fallback for development/testing
            console.log('Form submitted:', { ...formData, customSkills });
            alert('Thank you for your submission! We\'ll get back to you soon.');
        }
    };

    // For desktop
    const isLastPage = currentPage === totalPages - 1;
    const isFirstPage = currentPage === 0;

    // For mobile - determine which ContactSpecifics page to show
    const mobilePageIndex = currentMobilePage - 1; // ContactDetails is 0, so ContactSpecifics pages are 1-3
    const currentMobileContent = getPageContent(mobilePageIndex);

    // Mobile visibility logic
    const isMobileVisible = currentMobilePage >= 1 && currentMobilePage <= 3; // Back to 3 pages
    const isMobileLastPage = currentMobilePage === 3; // Back to 3 pages

    return (
        <div className={`contact-specifics ${isMobileVisible ? 'mobile-active' : 'mobile-hidden'}`}>
            <div className="form-section">
                {/* Desktop content */}
                <div className="desktop-content">
                    {currentContent.content}
                </div>

                {/* Mobile content */}
                <div className="mobile-content">
                    {currentMobileContent?.content}
                </div>
            </div>

            {/* Desktop navigation */}
            <div className="navigation-buttons desktop-navigation">
                <button
                    type="button"
                    onClick={prevPage}
                    disabled={isFirstPage}
                    aria-label="Previous step"
                >
                    Previous
                </button>

                <PageIndicator
                    currentPage={currentPage}
                    totalPages={totalPages}
                />

                {isLastPage ? (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="submit-button"
                        aria-label="Review form"
                        disabled={isSubmitting}
                    >
                        Review Application
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={nextPage}
                        aria-label="Next step"
                    >
                        Next
                    </button>
                )}
            </div>

            {/* Mobile navigation */}
            <div className="navigation-buttons mobile-navigation">
                <button
                    type="button"
                    onClick={handleMobilePrev}
                    aria-label="Previous step"
                >
                    Previous
                </button>

                <PageIndicator
                    currentPage={currentMobilePage}
                    totalPages={4}
                />

                {isMobileLastPage ? (
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="submit-button"
                        aria-label="Review form"
                        disabled={isSubmitting}
                    >
                        Review Application
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={handleMobileNext}
                        aria-label="Next step"
                    >
                        Next
                    </button>
                )}
            </div>
        </div>
    );
};

export default ContactSpecifics;
